import { ApiProperty } from '@nestjs/swagger';
import {
  IsString,
  IsNumber,
  IsBoolean,
  IsOptional,
  IsNotEmpty,
} from 'class-validator';

/**
 * DTO para respuestas de cliente individual
 * Utilizado en endpoints que retornan información de un cliente específico
 */
export class ClientResponseDto {
  @ApiProperty({
    example: 1,
    description: 'ID único del cliente',
  })
  @IsNumber()
  id!: number;

  @ApiProperty({
    example: 'Empresa Exportadora S.A.',
    description: 'Nombre completo del cliente',
  })
  @IsString()
  @IsNotEmpty()
  name!: string;

  @ApiProperty({
    example: 'EXP001',
    description: 'Código único del cliente para identificación',
  })
  @IsString()
  @IsNotEmpty()
  code!: string;

  @ApiProperty({
    example: true,
    description: 'Indica si el cliente está activo en el sistema',
  })
  @IsBoolean()
  is_active!: boolean;

  @ApiProperty({
    example: '2024-01-15T10:30:00Z',
    description: 'Fecha de creación del registro',
  })
  created_at!: Date;

  @ApiProperty({
    example: '2024-01-20T14:45:00Z',
    description: 'Fecha de última actualización',
    required: false,
  })
  @IsOptional()
  updated_at?: Date;
}

/**
 * DTO para respuestas de vista de cliente (SQL Server)
 * Utilizado en endpoints que consultan la vista Clientes del sistema OPB
 */
export class ClientViewResponseDto {
  @ApiProperty({
    example: 123,
    description: 'ID único del cliente en el sistema OPB',
  })
  @IsNumber()
  clienteId!: number;

  @ApiProperty({
    example: 'Empresa Importadora ABC',
    description: 'Nombre del cliente',
  })
  @IsString()
  @IsNotEmpty()
  cliente!: string;
}

/**
 * DTO para listado de clientes
 * Utilizado en endpoints que retornan múltiples clientes
 */
export class ClientListResponseDto {
  @ApiProperty({
    type: [ClientResponseDto],
    description: 'Lista de clientes disponibles',
  })
  clients!: ClientResponseDto[];

  @ApiProperty({
    example: 10,
    description: 'Total de clientes en la lista',
  })
  @IsNumber()
  total!: number;
}

/**
 * DTO para estadísticas de clientes
 * Utilizado en endpoints de reporting y dashboards
 */
export class ClientStatsResponseDto {
  @ApiProperty({
    example: 150,
    description: 'Total de clientes en el sistema',
  })
  @IsNumber()
  total!: number;

  @ApiProperty({
    type: [ClientViewResponseDto],
    description: 'Muestra representativa de clientes',
  })
  sample!: ClientViewResponseDto[];
}

/**
 * DTO para filtros de búsqueda de clientes
 * Utilizado en endpoints de búsqueda y filtrado
 */
export class ClientSearchFiltersDto {
  @ApiProperty({
    example: 'Exportadora',
    description: 'Texto para búsqueda por nombre (búsqueda parcial)',
    required: false,
  })
  @IsOptional()
  @IsString()
  nombre?: string;

  @ApiProperty({
    example: 123,
    description: 'ID específico del cliente',
    required: false,
  })
  @IsOptional()
  @IsNumber()
  clienteId?: number;

  @ApiProperty({
    example: 20,
    description: 'Límite de resultados por página',
    required: false,
    default: 20,
  })
  @IsOptional()
  @IsNumber()
  limit?: number;

  @ApiProperty({
    example: 0,
    description: 'Offset para paginación',
    required: false,
    default: 0,
  })
  @IsOptional()
  @IsNumber()
  offset?: number;
}
